// Serial Tag — every cpq2 config carries a customer-specific "Serial Tag" item
// as its own sales-order line, with a per-config line description. Two pieces:
//
//   resolveSerialTagItem() — asks /api/serial-tag which tag item id applies to
//     the launching customer (hard-coded map, else live lookup, else Flameco).
//   buildSerialTagDescription() — formats the line description from the answers,
//     per the spec:
//        Model:   the bare model — HOUSING_VALIDATION with the product-code prefix
//                 and the design suffix removed (`HBL-FAH26/18-16` -> `FAH26/18-16`).
//                 See shortModel; the Sales Description prints the same form.
//        BTU:     <Required Heat Duty: N.NMM when exact, else the full number>
//        Burner:  <Main Assembly Size>[ + " CI" if Assembly Type = Compound Injector]
//        Orifice: <Mixer Orifice Size>[ + inch indicator " | left as-is for MTD text]
//        PSI:     <Fuel Pressure>
//     (Serial # is intentionally omitted — the SERIAL_NUMBER field was removed.)

import type { AnswerState, Question } from "../types";
import { stripHtml } from "./text";

const API_BASE = "/api";

// SO-line constants for the tag line (per spec): qty 1, $0, tax code -7.
export const SERIAL_TAG_QUANTITY = 1;
export const SERIAL_TAG_AMOUNT = 0;
export const SERIAL_TAG_TAX_CODE = "-7";
export const SERIAL_TAG_FALLBACK_ID = "528080"; // Serial Tag - Flameco

export type SerialTagItem = { itemId: string; itemName: string; source: string };

export type SerialTagLine = {
  itemId: string;
  itemName: string;
  description: string;
  quantity: number;
  amount: number;
  taxCode: string;
};

/**
 * Ask the API which Serial Tag item applies to this customer. Never throws.
 *
 * Returns NULL when the tag could not be resolved, and that is the important part: it used
 * to substitute the Flameco fallback for ANY failure, which is what produced DUPLICATE
 * serial tags. The first submit resolved a customer's real tag; a later Edit Config hit a
 * transient lookup failure and got Flameco instead; a different item id is a different
 * sales-order line, so the order ended up with two. (The write-back is not at fault - it
 * dedupes by item id and cannot know the two ids mean the same role.)
 *
 * The server still answers Flameco when the lookup RAN and found nothing, because that is a
 * stable answer for a given customer. Only a genuine failure comes back unresolved, and the
 * caller then sends no tag at all: a missing line is reported and easy to add, whereas a
 * duplicate must be hunted down and deleted, and the wrong customer's tag on a shipped
 * order is worse than either.
 */
export function resolveSerialTagItem(
  customerId: string | null,
  customerName: string | null
): Promise<SerialTagItem | null> {
  // ONE LOOKUP PER CUSTOMER PER SESSION, warmed while the user configures (see
  // SaveSubmitButton). It was ~1.4s at the front of an Existing Config save's wait, for an
  // answer that depends on the customer alone.
  //
  // Only a RESOLVED tag is kept. A failure is dropped from the cache at once, so the submit
  // asks again rather than reusing it -- caching a failure would turn one transient blip into
  // "no tag line" for the rest of the session, which is exactly what the null contract above
  // exists to keep rare.
  const key = `${customerId ?? ""}\u0000${customerName ?? ""}`;
  const hit = tagLookups.get(key);
  if (hit) return hit;
  const p = fetchSerialTagItem(customerId, customerName).then((tag) => {
    if (!tag) tagLookups.delete(key);
    return tag;
  });
  tagLookups.set(key, p);
  return p;
}
const tagLookups = new Map<string, Promise<SerialTagItem | null>>();

async function fetchSerialTagItem(
  customerId: string | null,
  customerName: string | null
): Promise<SerialTagItem | null> {
  try {
    const qs = new URLSearchParams();
    if (customerId) qs.set("customerId", customerId);
    if (customerName) qs.set("customerName", customerName);
    const res = await fetch(`${API_BASE}/serial-tag?${qs.toString()}`, {
      headers: { Accept: "application/json" },
    });
    const body = (await res.json().catch(() => ({}))) as Partial<SerialTagItem> & {
      ok?: boolean;
      reason?: string;
    };
    if (res.ok && body.ok !== false && body.itemId) {
      return { itemId: String(body.itemId), itemName: body.itemName ?? "", source: body.source ?? "" };
    }
    console.log(
      `[cpq2] serial tag NOT resolved — no tag line will be added: ${
        body.reason ?? `HTTP ${res.status}`
      }`
    );
  } catch (e) {
    console.log(
      `[cpq2] serial tag NOT resolved — no tag line will be added: ${
        e instanceof Error ? e.message : String(e)
      }`
    );
  }
  return null;
}

// Label of the currently-selected option of a radio/select question (type 4),
// with HTML stripped — e.g. BURNER_ASSEMBLY_SIZE -> `5"`.
function selectedLabel(questions: Question[], answers: AnswerState, qCode: string): string {
  const q = questions.find((x) => x.custrecord_cpqc_question_code === qCode);
  if (!q) return "";
  for (const a of q._answers ?? []) {
    const code = a.custrecord_cpqc_answer_code;
    if (answers[`${qCode}/${code}`] === true) {
      return stripHtml(a.custrecord_cpqc_answer_label_html) || a.name || code;
    }
  }
  return "";
}

// Required Heat Duty (Btu/hr) display, the same rule as the mixer line's BTU/HR
// (formatBtuHr in additionalLines.ts). Under 1 million, in full and comma-grouped
// (300,000 -> "300,000"). At 1 million and up, "N.NMM" ONLY when that is exact
// (2,000,000 -> "2.0MM", 2,500,000 -> "2.5MM"); otherwise the full number, so
// 1,250,000 stays "1,250,000". It used to round every value to one decimal and printed
// 1,250,000 as "1.3MM" (Michael Swain, 2026-10-21: "the tag information is still rounding
// up the Btu"). Non-numeric or empty input is returned as-is so nothing is dropped.
function formatBtu(raw: unknown): string {
  const s = String(raw ?? "").trim();
  const n = Number(s.replace(/[,\s]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return s;
  if (n >= 1_000_000 && n % 100_000 === 0) return (n / 1_000_000).toFixed(1) + "MM";
  return n.toLocaleString("en-US");
}

// Mixer Orifice Size -> presentation. The stored value carries no explicit unit,
// and its meaning depends on whether it was auto-generated or typed by hand:
//
//   • Already marked -> left as entered: 1" stays 1"; any "…mtd…" -> "MTD <v>".
//   • Fraction / decimal (7/16, 0.375) -> a diameter in inches -> append ".
//   • AUTO-GENERATED bare number / letter -> an MTD drill from the capacity chart
//     ("DIA (in)" rows are fractional, "MTD" rows are numbers/letters) -> "MTD <v>".
//   • HAND-TYPED bare number -> inches (1 -> 1"); the user must type "mtd" to get
//     an MTD label.
//
// `autoValue` is MIXERORIFICE_MEMORY — the last value the sizing script wrote.
// The current value is auto-generated only while it still equals that shadow;
// once the user edits the field they differ, so we read the edit as inches.
function formatOrifice(raw: unknown, autoValue: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/["″]$/.test(s)) return s; // already inch-marked, e.g. 1"
  if (/mtd/i.test(s)) {
    const n = s.replace(/mtd/gi, "").trim(); // however typed ("40mtd" / "40 mtd" / "mtd 40") -> "MTD 40"
    return n ? "MTD " + n : "MTD";
  }
  if (/\//.test(s) || /\d\.\d/.test(s)) return s + '"'; // fraction/decimal diameter -> inches
  const autoGenerated = String(autoValue ?? "").trim() !== "" && s === String(autoValue).trim();
  if (autoGenerated && (/^\d+$/.test(s) || /^[A-Za-z]$/.test(s))) return "MTD " + s; // auto drill -> "MTD <v>"
  if (/^\d+$/.test(s)) return s + '"'; // hand-typed bare number -> inches
  return s; // anything else: leave as-is
}

/** Shorten a HOUSING_VALIDATION string to the bare model for display.
 *
 *  HOUSING_VALIDATION is built as `<productCode>-<SB|MB|FAH><size>[-<firetube><design>]`,
 *  e.g. `HBL-FAH26/18-16`, `BT-SB40/40-26LN`. Only the leading PRODUCT CODE comes
 *  off; everything after it prints as-is:
 *      HBL-FAH26/18-16  ->  FAH26/18-16
 *      BT-SB40/40-26LN  ->  SB40/40-26LN     (the design code LN is KEPT)
 *
 *  The design code is deliberately NOT stripped — confirmed by Kevin 2026-08-10
 *  after an earlier revision removed it.
 *
 *  The other half of "prefix and suffix" — the trailing `-<n>` config id on a
 *  created item's name (`HCL-FAH34/18-20-1`) — needs no code: HOUSING_VALIDATION is
 *  the pre-rename value and never carries it. Do NOT add a trailing-digits strip
 *  "for safety": the model's own tail IS `-<firetube diameter>`, so it would turn
 *  `FAH26/18-16` into `FAH26/18`.
 *
 *  The product code is only stripped when a qty code (SB/MB/FAH) follows it, so a
 *  value that has no product code can never lose its qty code. Verified against all
 *  38 distinct HOUSING_VALIDATION values live on items (recovered from
 *  custitem_fingerprint).
 *
 *  Kept in step with the identical helper in the DESCRIPTION operation
 *  (public/data/operations.json / cpq-operations/cpq2-description-builder.js) — the two
 *  cannot share code, since that one is a CPQ action script. */
export function shortModel(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .replace(/^[A-Za-z0-9]+-(?=(?:SB|MB|FAH))/, "") // drop the product code
    .replace(/^-/, ""); // no leading separator if there was no product code
}

/** Build the per-config Serial Tag sales-order LINE description. */
export function buildSerialTagDescription(answers: AnswerState, questions: Question[]): string {
  const model = shortModel(answers["HOUSING_VALIDATION/HOUSING_VALIDATION"]);
  const btu = formatBtu(answers["REQUIRED_HEAT_DUTY/REQUIRED_HEAT_DUTY"]);
  const size = selectedLabel(questions, answers, "BURNER_ASSEMBLY_SIZE");
  const isCompoundInjector = answers["MAIN_ASSEMBLY_TYPE/COMPOUND_INJECTOR"] === true;
  const burner = size ? size + (isCompoundInjector ? " CI" : "") : "";
  const orifice = formatOrifice(
    answers["MIXER_ORIFICE_SIZE/MIXER_ORIFICE_SIZE"],
    answers["MIXERORIFICE_MEMORY/MIXERORIFICE_MEMORY"]
  );
  const psi = String(answers["FUEL_PRESSURE/FUEL_PRESSURE"] ?? "").trim();

  return [
    `Model: ${model}`,
    `BTU: ${btu}`,
    `Burner: ${burner}`,
    `Orifice: ${orifice}`,
    `PSI: ${psi}`,
  ].join("\n");
}

/** Resolve the tag item and build its line in one call. Returns null when there
 *  is no tag to add (should not happen — resolve always falls back to Flameco). */
export async function buildSerialTagLine(
  answers: AnswerState,
  questions: Question[],
  customerId: string | null,
  customerName: string | null
): Promise<SerialTagLine | null> {
  const tag = await resolveSerialTagItem(customerId, customerName);
  // Unresolved -> no tag line at all, rather than a guessed one. See
  // resolveSerialTagItem for why a guess is what duplicated the tag on Edit Config.
  if (!tag || !tag.itemId) return null;
  return {
    itemId: tag.itemId,
    itemName: tag.itemName,
    description: buildSerialTagDescription(answers, questions),
    quantity: SERIAL_TAG_QUANTITY,
    amount: SERIAL_TAG_AMOUNT,
    taxCode: SERIAL_TAG_TAX_CODE,
  };
}
